/**
 * Client-side validation for the org template schema editor. Applies the same
 * ID rules and duplicate-derived-tag detection as the server
 * (backend/src/models/orgTemplate.ts), with matching wording, so the editor
 * can block a save inline instead of surfacing an API error.
 *
 * Duplicates are compared on *derived tag keys*, not raw IDs — 'a-b' and
 * 'a_b' both derive 'a_b_enabled'; order forms '1A' and '1a' lowercase to the
 * same key; a module and a schedule with the same key share one tag.
 */

import type { OrgTemplateSchema } from '../types/orgTemplate';
import {
  MODULE_ID_PATTERN,
  MODULE_ID_RULE,
  SCHEDULE_ID_PATTERN,
  SCHEDULE_ID_RULE,
  ORDER_FORM_ID_PATTERN,
  ORDER_FORM_ID_RULE,
  moduleEnabledTag,
  orderFormEnabledTag,
  scheduleEnabledTag,
  duplicateTagMessage,
} from './templateTags';

export interface OrgSchemaValidationResult {
  /** Module index → error message for that module's ID field. */
  moduleIdErrors: Record<number, string>;
  /** `${moduleIndex}.${orderFormIndex}` → error message for that order form's ID field. */
  orderFormIdErrors: Record<string, string>;
  /** Schedule index → error message for that schedule's ID field. */
  scheduleIdErrors: Record<number, string>;
  hasErrors: boolean;
}

export function validateOrgTemplateSchema(schema: OrgTemplateSchema): OrgSchemaValidationResult {
  const moduleIdErrors: Record<number, string> = {};
  const orderFormIdErrors: Record<string, string> = {};
  const scheduleIdErrors: Record<number, string> = {};

  // Tags claimed by modules and schedules share one namespace ('<key>_enabled').
  const sectionTags = new Map<string, string>();

  schema.modules.forEach((mod, moduleIndex) => {
    if (!MODULE_ID_PATTERN.test(mod.id)) {
      moduleIdErrors[moduleIndex] = `Module ID ${MODULE_ID_RULE}`;
    } else {
      const tag = moduleEnabledTag(mod.id);
      const existing = sectionTags.get(tag);
      if (existing) {
        moduleIdErrors[moduleIndex] = `Module ID '${mod.id}' ${duplicateTagMessage(tag, existing)}`;
      } else {
        sectionTags.set(tag, `module '${mod.name || mod.id}'`);
      }
    }

    const orderFormTags = new Map<string, string>();
    mod.orderForms.forEach((orderForm, orderFormIndex) => {
      const key = `${moduleIndex}.${orderFormIndex}`;
      if (!ORDER_FORM_ID_PATTERN.test(orderForm.id)) {
        orderFormIdErrors[key] = `Order form ID ${ORDER_FORM_ID_RULE}`;
        return;
      }
      const tag = orderFormEnabledTag(mod.id, orderForm.id);
      const existing = orderFormTags.get(tag);
      if (existing) {
        orderFormIdErrors[key] = `Order form ID '${orderForm.id}' ${duplicateTagMessage(tag, existing)}`;
      } else {
        orderFormTags.set(tag, `order form '${orderForm.name || orderForm.id}'`);
      }
    });
  });

  schema.schedules.forEach((schedule, scheduleIndex) => {
    if (!SCHEDULE_ID_PATTERN.test(schedule.id)) {
      scheduleIdErrors[scheduleIndex] = `Schedule ID ${SCHEDULE_ID_RULE}`;
      return;
    }
    const tag = scheduleEnabledTag(schedule.id);
    const existing = sectionTags.get(tag);
    if (existing) {
      scheduleIdErrors[scheduleIndex] = `Schedule ID '${schedule.id}' ${duplicateTagMessage(tag, existing)}`;
    } else {
      sectionTags.set(tag, `schedule '${schedule.name || schedule.id}'`);
    }
  });

  const hasErrors =
    Object.keys(moduleIdErrors).length > 0 ||
    Object.keys(orderFormIdErrors).length > 0 ||
    Object.keys(scheduleIdErrors).length > 0;

  return { moduleIdErrors, orderFormIdErrors, scheduleIdErrors, hasErrors };
}
